// Events.jsx — Sponsorships + events tracker with departmental requirements.
import React, { useEffect, useState, useCallback } from "react";
import { api, formatErr } from "./lib/api";
import { useAuth } from "./lib/auth";
import { confirmWithUndo, showToast } from "./lib/theme";
import { AssigneeSelect } from "./lib/users";
import DateField from "./components/DateField";

const TYPE_LABELS = { event: "🎉 فعالية", sponsorship: "🤝 رعاية", partnership: "🔗 شراكة" };
const STATUS_LABELS = {
  planned: { lbl: "📅 مخطّطة", color: "var(--info)" },
  ongoing: { lbl: "🟢 جارية", color: "var(--success)" },
  done: { lbl: "✅ منتهية", color: "var(--muted)" },
  cancelled: { lbl: "❌ ملغاة", color: "var(--danger)" },
};
const REQ_STATUS = { open: "🆕 جديد", in_progress: "⏳ جارٍ", done: "✅ منجز", blocked: "🚧 معطّل" };
const DEPTS = ["تسويق", "تشغيل", "مالية", "مخزون", "موارد بشرية", "تقني", "أخرى"];

function fm(n) { return Math.round(Number(n) || 0).toLocaleString("en-US"); }

export default function Events() {
  const { user, hasPerm } = useAuth();
  const canCreate = true;
  const canEdit = user.role === "admin" || hasPerm("marketing.events");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", type: "event", date: new Date().toISOString().slice(0, 10), location: "", target_attendees: 0, budget_sar: 0, sponsor: "", description: "" });
  const [expanded, setExpanded] = useState(null);
  const [reqForm, setReqForm] = useState({ department: "تسويق", item: "", owner: "", due_date: "", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/events"); setItems(data.items || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/events", form);
      showToast({ kind: "success", message: `✅ تم إنشاء "${form.name}"` });
      setForm({ name: "", type: "event", date: new Date().toISOString().slice(0, 10), location: "", target_attendees: 0, budget_sar: 0, sponsor: "", description: "" });
      setShowForm(false); load();
    } catch (e) { showToast({ kind: "danger", message: formatErr(e.response?.data?.detail) || e.message }); }
  };

  const addReq = async (ev) => {
    if (!reqForm.item.trim() || !reqForm.owner.trim()) {
      showToast({ kind: "danger", message: "الوصف والمسؤول مطلوبان" }); return;
    }
    const newReq = { ...reqForm, id: crypto.randomUUID(), status: "open" };
    const reqs = [...(ev.requirements || []), newReq];
    await api.patch(`/events/${ev.id}`, { requirements: reqs });
    setReqForm({ department: "تسويق", item: "", owner: "", due_date: "", notes: "" });
    load();
  };

  const updateReq = async (ev, rid, updates) => {
    const reqs = (ev.requirements || []).map((r) => r.id === rid ? { ...r, ...updates } : r);
    await api.patch(`/events/${ev.id}`, { requirements: reqs }); load();
  };

  const deleteReq = (ev, rid) => {
    confirmWithUndo({
      message: "🗑 حذف المتطلب",
      onConfirm: async () => {
        const reqs = (ev.requirements || []).filter((r) => r.id !== rid);
        await api.patch(`/events/${ev.id}`, { requirements: reqs }); load();
      },
      onUndo: () => {},
    });
  };

  const remove = (ev) => {
    setItems((cur) => cur.filter((x) => x.id !== ev.id));
    confirmWithUndo({
      message: `🗑 حذف "${ev.name}"`,
      onConfirm: async () => { try { await api.delete(`/events/${ev.id}`); } catch (e) {} load(); },
      onUndo: load,
    });
  };

  const updateStatus = async (ev, status) => { await api.patch(`/events/${ev.id}`, { status }); load(); };
  const updateAttendees = async (ev, actual) => { await api.patch(`/events/${ev.id}`, { actual_attendees: actual }); load(); };

  return (
    <div data-testid="events-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div className="card" style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <div>
          <h3 style={{ margin: "0 0 4px", color: "var(--brown-deep)" }}>🎉 الفعاليات والرعايات</h3>
          <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>سجّل كل الفعاليات + الرعايات + المتطلبات لكل قسم مع المسؤول</p>
        </div>
        {canCreate && <button className="btn btn-primary" onClick={() => setShowForm(!showForm)} data-testid="toggle-event-form">{showForm ? "إلغاء" : "+ جديدة"}</button>}
      </div>

      {showForm && canCreate && (
        <form className="card" onSubmit={submit} data-testid="event-form" style={{ marginBottom: 14 }}>
          <h4 style={{ margin: "0 0 10px", color: "var(--brown-deep)" }}>➕ فعالية / رعاية</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
            <div><label style={{ fontSize: 11 }}>الاسم *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ev-name" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>النوع</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="ev-type" style={{ width: "100%" }}>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11 }}>التاريخ *</label><DateField  required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="ev-date" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>الموقع</label><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="ev-location" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>الحضور المستهدف</label><input type="number" min={0} value={form.target_attendees} onChange={(e) => setForm({ ...form, target_attendees: Number(e.target.value) || 0 })} data-testid="ev-target" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>الميزانية (ر)</label><input type="number" min={0} value={form.budget_sar} onChange={(e) => setForm({ ...form, budget_sar: Number(e.target.value) || 0 })} data-testid="ev-budget" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>الراعي/الجهة</label><input value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })} data-testid="ev-sponsor" style={{ width: "100%" }} /></div>
          </div>
          <div style={{ marginTop: 8 }}><label style={{ fontSize: 11 }}>الوصف</label><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ev-desc" style={{ width: "100%" }} /></div>
          <button type="submit" className="btn btn-success" data-testid="ev-submit" style={{ marginTop: 10 }}>💾 احفظ</button>
        </form>
      )}

      {loading ? <div style={{ padding: 20, textAlign: "center" }}>⏳</div> : items.length === 0 ? (
        <div className="card" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>لا توجد فعاليات بعد</div>
      ) : items.map((ev) => {
        const status = STATUS_LABELS[ev.status] || STATUS_LABELS.planned;
        const doneReqs = (ev.requirements || []).filter((r) => r.status === "done").length;
        const totalReqs = (ev.requirements || []).length;
        const attendeePct = ev.target_attendees ? Math.round((ev.actual_attendees || 0) / ev.target_attendees * 100) : 0;
        return (
          <div key={ev.id} className="card" style={{ marginBottom: 10 }} data-testid={`ev-${ev.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>{TYPE_LABELS[ev.type]} {ev.name}</h4>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                  📅 {ev.date} {ev.location && `· 📍 ${ev.location}`} {ev.sponsor && `· 🤝 ${ev.sponsor}`}
                  {ev.budget_sar > 0 && ` · 💵 ${fm(ev.budget_sar)} ر`}
                  {totalReqs > 0 && ` · ✅ ${doneReqs}/${totalReqs} متطلب`}
                </div>
                {ev.target_attendees > 0 && (
                  <div style={{ fontSize: 11, marginTop: 4 }}>
                    👥 المستهدف {fm(ev.target_attendees)} · الفعلي <b>{fm(ev.actual_attendees || 0)}</b>
                    <span style={{ color: attendeePct >= 100 ? "var(--success)" : attendeePct >= 70 ? "var(--warn)" : "var(--danger)", marginInlineStart: 6 }}>({attendeePct}%)</span>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ padding: "3px 8px", background: status.color, color: "#fff", borderRadius: 4, fontSize: 11 }}>{status.lbl}</span>
                <button className="btn btn-soft" onClick={() => setExpanded(expanded === ev.id ? null : ev.id)} data-testid={`ev-toggle-${ev.id}`} style={{ fontSize: 11 }}>{expanded === ev.id ? "🔼" : "🔽 المتطلبات"}</button>
                {canEdit && <>
                  <select value={ev.status || "planned"} onChange={(e) => updateStatus(ev, e.target.value)} style={{ fontSize: 11 }} data-testid={`ev-status-${ev.id}`}>{Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.lbl}</option>)}</select>
                  <button onClick={() => remove(ev)} className="btn" data-testid={`ev-delete-${ev.id}`} style={{ background: "var(--danger)", color: "#fff", fontSize: 11 }}>🗑</button>
                </>}
              </div>
            </div>

            {expanded === ev.id && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)" }}>
                {canEdit && ev.target_attendees > 0 && (
                  <div style={{ marginBottom: 8, fontSize: 12 }}>
                    📊 الحضور الفعلي: <input type="number" min={0} value={ev.actual_attendees || 0} onChange={(e) => updateAttendees(ev, Number(e.target.value) || 0)} data-testid={`ev-actual-${ev.id}`} style={{ width: 100, marginInlineStart: 6 }} />
                  </div>
                )}
                {ev.description && <div style={{ marginBottom: 10, padding: 8, background: "var(--beige)", borderRadius: 4, fontSize: 12 }}>📝 {ev.description}</div>}

                <b style={{ fontSize: 12, color: "var(--brown-deep)", display: "block", marginBottom: 6 }}>📋 المتطلبات حسب الإدارة/القسم</b>

                {totalReqs > 0 && (
                  <div className="tw" style={{ marginBottom: 10 }}>
                    <table style={{ fontSize: 12 }}>
                      <thead><tr><th>القسم</th><th>المتطلب</th><th>المسؤول</th><th>الموعد</th><th>الحالة</th><th></th></tr></thead>
                      <tbody>
                        {(ev.requirements || []).map((r) => (
                          <tr key={r.id} data-testid={`req-${r.id}`}>
                            <td><span style={{ background: "var(--info-soft)", color: "var(--info)", padding: "2px 8px", borderRadius: 4, fontSize: 11 }}>{r.department}</span></td>
                            <td>{r.item}{r.notes && <div style={{ fontSize: 10, color: "var(--muted)" }}>{r.notes}</div>}</td>
                            <td><b>{r.owner}</b></td>
                            <td style={{ fontSize: 11 }}>{r.due_date || "—"}</td>
                            <td>{canEdit ? (
                              <select value={r.status || "open"} onChange={(e) => updateReq(ev, r.id, { status: e.target.value })} data-testid={`req-status-${r.id}`} style={{ fontSize: 11 }}>{Object.entries(REQ_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
                            ) : REQ_STATUS[r.status] || REQ_STATUS.open}</td>
                            <td>{canEdit && <button onClick={() => deleteReq(ev, r.id)} className="btn" data-testid={`req-delete-${r.id}`} style={{ background: "transparent", color: "var(--danger)", padding: "2px 6px" }}>×</button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {canEdit && (
                  <div style={{ background: "var(--beige)", padding: 8, borderRadius: 4, display: "grid", gridTemplateColumns: "1fr 2fr 1fr 1fr auto", gap: 6, alignItems: "end" }}>
                    <div><label style={{ fontSize: 10 }}>القسم</label>
                      <select value={reqForm.department} onChange={(e) => setReqForm({ ...reqForm, department: e.target.value })} data-testid="new-req-dept" style={{ width: "100%", fontSize: 11 }}>
                        {DEPTS.map((d) => <option key={d}>{d}</option>)}
                      </select>
                    </div>
                    <div><label style={{ fontSize: 10 }}>المتطلب</label><input value={reqForm.item} onChange={(e) => setReqForm({ ...reqForm, item: e.target.value })} data-testid="new-req-item" style={{ width: "100%", fontSize: 12 }} /></div>
                    <AssigneeSelect value={reqForm.owner} onChange={(v) => setReqForm({ ...reqForm, owner: v })} label="المسؤول" testid="new-req-owner" />
                    <div><label style={{ fontSize: 10 }}>الموعد</label><DateField  value={reqForm.due_date} onChange={(e) => setReqForm({ ...reqForm, due_date: e.target.value })} data-testid="new-req-due" style={{ width: "100%", fontSize: 12 }} /></div>
                    <button className="btn btn-success" onClick={() => addReq(ev)} data-testid={`add-req-${ev.id}`} style={{ fontSize: 11 }}>+ إضافة</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
